import {
  Color3,
  Color4,
  DynamicTexture,
  GPUParticleSystem,
  Mesh,
  Observer,
  ParticleSystem,
  PBRMaterial,
  Scene,
  Texture,
  Vector3,
} from "@babylonjs/core";
import { isTouchDevice } from "../util/device.js";

/**
 * Fountain water: a scrolling procedural ripple normal on a low-roughness
 * PBR surface, plus particle jets.
 *
 * Deliberately NOT Babylon's WaterMaterial: that needs reflection AND
 * refraction render targets, i.e. two extra scene passes every frame, which
 * the Quest cannot pay for. The IBL environment already gives the surface
 * believable sky/skylight reflections, and the animated normal supplies the
 * motion the eye actually reads as "flowing".
 */

const RIPPLE_TEX_SIZE: number = 256;
/** Metres of texture per tile; the basin is ~7 m across. */
const RIPPLE_UV_SCALE: number = 6;
const SCROLL_U_PER_SEC: number = 0.035;
const SCROLL_V_PER_SEC: number = 0.021;

/**
 * Tileable normal map built from a few summed sine waves at different
 * angles, so the surface never shows an obvious single wave direction.
 * Procedural on purpose: no texture download, and it still works under
 * NullEngine where there is no document.
 */
export function createRippleNormal(scene: Scene, name: string): DynamicTexture | null {
  if (typeof document === "undefined") {
    return null;
  }
  const tex: DynamicTexture = new DynamicTexture(
    name, { width: RIPPLE_TEX_SIZE, height: RIPPLE_TEX_SIZE }, scene, true
  );
  const ctx: CanvasRenderingContext2D = tex.getContext() as CanvasRenderingContext2D;
  const image: ImageData = ctx.createImageData(RIPPLE_TEX_SIZE, RIPPLE_TEX_SIZE);
  // Wave set: [frequency (whole cycles across the tile), angle, amplitude].
  const waves: Array<[number, number, number]> = [
    [3, 0.0, 1.0],
    [5, 1.1, 0.6],
    [7, 2.3, 0.35],
    [11, 0.6, 0.18],
  ];
  const height = (x: number, y: number): number => {
    let h: number = 0;
    for (const [freq, angle, amp] of waves) {
      const u: number = (x * Math.cos(angle) + y * Math.sin(angle)) / RIPPLE_TEX_SIZE;
      h += Math.sin(u * Math.PI * 2 * freq) * amp;
    }
    return h;
  };
  for (let y: number = 0; y < RIPPLE_TEX_SIZE; y++) {
    for (let x: number = 0; x < RIPPLE_TEX_SIZE; x++) {
      // Central differences give the slope; pack it as a tangent-space normal.
      const dx: number = height(x + 1, y) - height(x - 1, y);
      const dy: number = height(x, y + 1) - height(x, y - 1);
      const nx: number = -dx * 0.5;
      const ny: number = -dy * 0.5;
      const nz: number = 1;
      const len: number = Math.hypot(nx, ny, nz);
      const i: number = (y * RIPPLE_TEX_SIZE + x) * 4;
      image.data[i] = ((nx / len) * 0.5 + 0.5) * 255;
      image.data[i + 1] = ((ny / len) * 0.5 + 0.5) * 255;
      image.data[i + 2] = ((nz / len) * 0.5 + 0.5) * 255;
      image.data[i + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  tex.update();
  tex.uScale = RIPPLE_UV_SCALE;
  tex.vScale = RIPPLE_UV_SCALE;
  tex.wrapU = Texture.WRAP_ADDRESSMODE;
  tex.wrapV = Texture.WRAP_ADDRESSMODE;
  return tex;
}

/** Advances the ripple scroll. Split out from the render observer so the
 *  motion is testable without a real engine clock (NullEngine reports a
 *  delta of 0 forever). Offsets wrap into [0, 1) to stay float-stable over a
 *  long session. */
export function advanceRipple(texture: Texture, dtSeconds: number): void {
  texture.uOffset = (texture.uOffset + SCROLL_U_PER_SEC * dtSeconds) % 1;
  texture.vOffset = (texture.vOffset + SCROLL_V_PER_SEC * dtSeconds) % 1;
}

export interface WaterHandle {
  material: PBRMaterial;
  dispose(): void;
}

/**
 * Pool-water material. Semi-transparent so the basin floor tint reads
 * through, low roughness so the skylight streaks across it, and its normal
 * map scrolls every frame.
 */
export function createWaterMaterial(scene: Scene, name: string): WaterHandle {
  const mat: PBRMaterial = new PBRMaterial(name, scene);
  mat.albedoColor = new Color3(0.06, 0.19, 0.24);
  mat.metallic = 0.1;
  mat.roughness = 0.08;
  mat.alpha = 0.72;
  mat.backFaceCulling = true;
  // A tinted pool still catches the skylight; without this the surface goes
  // flat black wherever the environment is dark.
  mat.environmentIntensity = 1.4;

  const ripple: DynamicTexture | null = createRippleNormal(scene, `${name}-normal`);
  let observer: Observer<Scene> | null = null;
  if (ripple !== null) {
    mat.bumpTexture = ripple;
    mat.bumpTexture.level = 0.65;
    observer = scene.onBeforeRenderObservable.add(() => {
      advanceRipple(ripple, scene.getEngine().getDeltaTime() / 1000);
    });
  }

  return {
    material: mat,
    dispose: (): void => {
      if (observer !== null) {
        scene.onBeforeRenderObservable.remove(observer);
      }
      ripple?.dispose();
      mat.dispose();
    },
  };
}

/** Small white dot, so jets/mist need no texture download either. */
function createDropletTexture(scene: Scene, name: string): DynamicTexture | null {
  if (typeof document === "undefined") {
    return null;
  }
  const size: number = 32;
  const tex: DynamicTexture = new DynamicTexture(name, { width: size, height: size }, scene, true);
  const ctx: CanvasRenderingContext2D = tex.getContext() as CanvasRenderingContext2D;
  const grad: CanvasGradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.4, "rgba(235,248,255,0.75)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  tex.update();
  tex.hasAlpha = true;
  return tex;
}

export interface FountainJetOptions {
  /** World-space nozzle position. */
  origin: Vector3;
  /** Direction the arc is thrown in (need not be normalized). */
  direction: Vector3;
  /** Override the touch-device probe (tests). */
  touch?: boolean;
}

/**
 * One arcing jet of water. GPU particles on desktop, a much smaller CPU
 * system on phones/Quest where GPU particles are either unsupported or not
 * worth the bandwidth.
 */
export function createFountainJet(
  scene: Scene,
  name: string,
  options: FountainJetOptions
): ParticleSystem | GPUParticleSystem | null {
  const texture: DynamicTexture | null = createDropletTexture(scene, `${name}-tex`);
  if (texture === null) {
    return null;
  }
  const touch: boolean = options.touch ?? isTouchDevice();
  const capacity: number = touch ? 220 : 700;
  const useGpu: boolean = !touch && GPUParticleSystem.IsSupported;
  const system: ParticleSystem | GPUParticleSystem = useGpu
    ? new GPUParticleSystem(name, { capacity }, scene)
    : new ParticleSystem(name, capacity, scene);

  system.particleTexture = texture;
  system.emitter = options.origin.clone();
  system.minEmitBox = new Vector3(-0.03, 0, -0.03);
  system.maxEmitBox = new Vector3(0.03, 0, 0.03);

  const dir: Vector3 = options.direction.normalize();
  system.direction1 = dir.scale(0.85);
  system.direction2 = dir.scale(1.15);

  system.color1 = new Color4(0.85, 0.95, 1.0, 0.85);
  system.color2 = new Color4(0.7, 0.88, 1.0, 0.6);
  system.colorDead = new Color4(0.8, 0.9, 1.0, 0);

  system.minSize = 0.03;
  system.maxSize = 0.09;
  system.minLifeTime = 0.6;
  system.maxLifeTime = 1.1;
  system.emitRate = touch ? 120 : 380;
  system.minEmitPower = 2.6;
  system.maxEmitPower = 3.4;
  system.updateSpeed = 0.016;
  // Real gravity, so the arc falls back into the basin instead of drifting.
  system.gravity = new Vector3(0, -9.81, 0);
  system.blendMode = ParticleSystem.BLENDMODE_STANDARD;
  system.start();
  return system;
}

/** Low, slow haze sitting on the water where the jets land. */
export function createFountainMist(
  scene: Scene,
  name: string,
  center: Vector3,
  radius: number,
  touchOverride?: boolean
): ParticleSystem | null {
  const texture: DynamicTexture | null = createDropletTexture(scene, `${name}-tex`);
  if (texture === null) {
    return null;
  }
  const touch: boolean = touchOverride ?? isTouchDevice();
  const system: ParticleSystem = new ParticleSystem(name, touch ? 60 : 160, scene);
  system.particleTexture = texture;
  system.emitter = center.clone();
  system.minEmitBox = new Vector3(-radius, 0, -radius);
  system.maxEmitBox = new Vector3(radius, 0.1, radius);
  system.direction1 = new Vector3(-0.05, 0.25, -0.05);
  system.direction2 = new Vector3(0.05, 0.5, 0.05);
  system.color1 = new Color4(1, 1, 1, 0.14);
  system.color2 = new Color4(0.9, 0.96, 1, 0.08);
  system.colorDead = new Color4(1, 1, 1, 0);
  system.minSize = 0.25;
  system.maxSize = 0.7;
  system.minLifeTime = 1.4;
  system.maxLifeTime = 2.6;
  system.emitRate = touch ? 8 : 22;
  system.minEmitPower = 0.1;
  system.maxEmitPower = 0.3;
  system.blendMode = ParticleSystem.BLENDMODE_STANDARD;
  system.start();
  return system;
}

/** Solid basin floor tint under the water plane, so the pool has depth. */
export function createBasinFloorMaterial(scene: Scene, name: string): PBRMaterial {
  const mat: PBRMaterial = new PBRMaterial(name, scene);
  mat.albedoColor = new Color3(0.1, 0.16, 0.18);
  mat.metallic = 0;
  mat.roughness = 0.55;
  return mat;
}

/** Convenience for zone code: mark a water mesh as receive-only. */
export function markWaterMesh(mesh: Mesh): void {
  mesh.isPickable = false;
  mesh.receiveShadows = true;
}
